import React from 'react';

import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';

export type ServingUnit = '' | 'g' | 'ml' | 'item';

export type FoodFormData = {
  name_en: string;
  name_ar: string;
  brand_name_en: string;
  brand_name_ar: string;
  barcode: string;
  image_url: string;
  description: string;
  serving_size: string;
  serving_unit: ServingUnit;
  calories: string;
  fat_g: string;
  protein_g: string;
  carbohydrates_g: string;
  fiber_g: string;
  sugar_g: string;
  sodium_mg: string;
  trans_fat_g: string;
  saturated_fat_g: string;
  cholesterol_mg: string;
  added_sugars_g: string;
  vitamin_d_mcg: string;
  calcium_mg: string;
  iron_mg: string;
  potassium_mg: string;
  vitamin_a_rae_mcg: string;
  vitamin_c_mg: string;
  vitamin_b6_mg: string;
  vitamin_b12_mcg: string;
  magnesium_mg: string;
  zinc_mg: string;
  phosphorus_mg: string;
  folate_mcg: string;
  water_g: string;
  ash_g: string;
  vitamin_e_mg: string;
  vitamin_k_mcg: string;
  thiamin_mg: string;
  riboflavin_mg: string;
  niacin_mg: string;
  pantothenic_acid_mg: string;
  copper_mg: string;
  manganese_mg: string;
  selenium_mcg: string;
  monounsaturated_fat_g: string;
  polyunsaturated_fat_g: string;
  caffeine_mg: string;
};

type FoodFormSectionProps = {
  formData: FoodFormData;
  onChange: (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  isEditMode: boolean;
  loadingFood: boolean;
  saving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

type InputProps = {
  label: string;
  name: keyof FoodFormData;
  value: string;
  onChange: FoodFormSectionProps['onChange'];
  placeholder?: string;
  type?: 'text' | 'number';
  dir?: 'rtl';
  min?: string;
  step?: string;
  rows?: number;
  required?: boolean;
};

const TextField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  dir,
  type = 'text',
  required
}: InputProps) => (
  <Col md={6}>
    <label className="form-label">
      {label}
      {required && <span className="text-danger ms-1">*</span>}
    </label>
    <input
      type={type}
      name={name}
      className="form-control"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      dir={dir}
      required={required}
    />
  </Col>
);

const NumberField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  min = '0',
  step = 'any'
}: InputProps) => (
  <Col md={4}>
    <label className="form-label">{label}</label>
    <input
      type="number"
      name={name}
      className="form-control"
      min={min}
      step={step}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </Col>
);

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 3
}: InputProps) => (
  <Col xs={12}>
    <label className="form-label">{label}</label>
    <textarea
      name={name}
      className="form-control"
      rows={rows}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </Col>
);

const SectionCard = ({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <Col xs={12}>
    <Card>
      <Card.Header>
        <h5 className="mb-1">{title}</h5>
        <p className="text-body-secondary mb-0 fs-9">{description}</p>
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  </Col>
);

const FoodActionBar = ({
  isEditMode,
  saving,
  loadingFood,
  onSubmit,
  onCancel
}: Pick<
  FoodFormSectionProps,
  'isEditMode' | 'saving' | 'loadingFood' | 'onSubmit' | 'onCancel'
>) => (
  <Col xs={12}>
    <div className="d-flex justify-content-end gap-2">
      <Button variant="outline-secondary" onClick={onCancel}>
        Cancel
      </Button>

      <Button
        variant="primary"
        type="button"
        onClick={onSubmit}
        disabled={saving || loadingFood}
      >
        {saving ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            {isEditMode ? 'Updating...' : 'Saving...'}
          </>
        ) : isEditMode ? (
          'Update Food'
        ) : (
          'Save Food'
        )}
      </Button>
    </div>
  </Col>
);

export const FoodFormSections = ({
  formData,
  onChange,
  isEditMode,
  loadingFood,
  saving,
  onSubmit,
  onCancel
}: FoodFormSectionProps) => (
  <div className="mb-9">
    <Row className="g-3">
      <Col xs={12}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-1">{isEditMode ? 'Edit Food' : 'Add Food'}</h3>
            <p className="text-body-secondary mb-0">
              {isEditMode
                ? 'Update food information and nutrition details.'
                : 'Add a new food to the MLAS food database.'}
            </p>
          </div>

          <Button variant="outline-secondary" size="sm" onClick={onCancel}>
            Back
          </Button>
        </div>
      </Col>

      <SectionCard
        title="Basic Information"
        description="Enter the basic food and brand information."
      >
        <Row className="g-3">
          <TextField
            label="Food Name (English)"
            name="name_en"
            value={formData.name_en}
            onChange={onChange}
            placeholder="Enter food name"
            required={true}
          />
          <TextField
            label="Food Name (Arabic)"
            name="name_ar"
            value={formData.name_ar}
            onChange={onChange}
            placeholder="Enter Arabic food name"
            dir="rtl"
            required={true}
          />
          <TextField
            label="Brand Name (English)"
            name="brand_name_en"
            value={formData.brand_name_en}
            onChange={onChange}
            placeholder="Enter brand name"
          />
          <TextField
            label="Brand Name (Arabic)"
            name="brand_name_ar"
            value={formData.brand_name_ar}
            onChange={onChange}
            placeholder="Enter Arabic brand name"
            dir="rtl"
          />
          <TextField
            label="Barcode"
            name="barcode"
            value={formData.barcode}
            onChange={onChange}
            placeholder="Enter barcode"
          />
          <TextField
            label="Image URL"
            name="image_url"
            value={formData.image_url}
            onChange={onChange}
            placeholder="Enter image URL"
          />
          <TextAreaField
            label="Description"
            name="description"
            value={formData.description}
            onChange={onChange}
            placeholder="Enter food description"
          />
        </Row>
      </SectionCard>

      <SectionCard
        title="Serving Information"
        description="Define the default serving size for this food."
      >
        <Row className="g-3">
          <Col md={6}>
            <label className="form-label">Serving Size</label>
            <input
              type="number"
              name="serving_size"
              className="form-control"
              placeholder="Enter serving size"
              min="0"
              step="any"
              value={formData.serving_size}
              onChange={onChange}
            />
          </Col>

          <Col md={6}>
            <label className="form-label">Serving Unit</label>
            <select
              name="serving_unit"
              className="form-select"
              value={formData.serving_unit}
              onChange={onChange}
            >
              <option value="">Select unit</option>
              <option value="g">g</option>
              <option value="ml">ml</option>
              <option value="item">item</option>
            </select>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard
        title="Core Nutrition"
        description="Enter the main nutritional values."
      >
        <Row className="g-3">
          <NumberField
            label="Energy (kcal)"
            name="calories"
            value={formData.calories}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Total Fat (g)"
            name="fat_g"
            value={formData.fat_g}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Protein (g)"
            name="protein_g"
            value={formData.protein_g}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Total Carbohydrate (g)"
            name="carbohydrates_g"
            value={formData.carbohydrates_g}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Dietary Fiber (g)"
            name="fiber_g"
            value={formData.fiber_g}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Total Sugars (g)"
            name="sugar_g"
            value={formData.sugar_g}
            onChange={onChange}
            placeholder="Enter value"
          />
          <NumberField
            label="Sodium (mg)"
            name="sodium_mg"
            value={formData.sodium_mg}
            onChange={onChange}
            placeholder="Enter value"
          />
        </Row>
      </SectionCard>

      <SectionCard
        title="Additional Nutrition"
        description="Enter additional nutritional information. Leave fields empty when the value is not available."
      >
        <div className="mb-4">
          <h6 className="mb-3">Fat & Sugar</h6>
          <Row className="g-3">
            <NumberField
              label="Trans Fat (g)"
              name="trans_fat_g"
              value={formData.trans_fat_g}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Saturated Fat (g)"
              name="saturated_fat_g"
              value={formData.saturated_fat_g}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Cholesterol (mg)"
              name="cholesterol_mg"
              value={formData.cholesterol_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Added Sugars (g)"
              name="added_sugars_g"
              value={formData.added_sugars_g}
              onChange={onChange}
              placeholder="Enter value"
            />
          </Row>
        </div>

        <div className="mb-4">
          <h6 className="mb-3">Vitamins & Minerals</h6>
          <Row className="g-3">
            <NumberField
              label="Vitamin D (mcg)"
              name="vitamin_d_mcg"
              value={formData.vitamin_d_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Calcium (mg)"
              name="calcium_mg"
              value={formData.calcium_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Iron (mg)"
              name="iron_mg"
              value={formData.iron_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Potassium (mg)"
              name="potassium_mg"
              value={formData.potassium_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin A, RAE (mcg)"
              name="vitamin_a_rae_mcg"
              value={formData.vitamin_a_rae_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin C (mg)"
              name="vitamin_c_mg"
              value={formData.vitamin_c_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin B6 (mg)"
              name="vitamin_b6_mg"
              value={formData.vitamin_b6_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin B12 (mcg)"
              name="vitamin_b12_mcg"
              value={formData.vitamin_b12_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Magnesium (mg)"
              name="magnesium_mg"
              value={formData.magnesium_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Zinc (mg)"
              name="zinc_mg"
              value={formData.zinc_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Phosphorus (mg)"
              name="phosphorus_mg"
              value={formData.phosphorus_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Folate (mcg)"
              name="folate_mcg"
              value={formData.folate_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
          </Row>
        </div>

        <div className="mb-4">
          <h6 className="mb-3">Other Nutrients</h6>
          <Row className="g-3">
            <NumberField
              label="Water (g)"
              name="water_g"
              value={formData.water_g}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Ash (g)"
              name="ash_g"
              value={formData.ash_g}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin E (mg)"
              name="vitamin_e_mg"
              value={formData.vitamin_e_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Vitamin K (mcg)"
              name="vitamin_k_mcg"
              value={formData.vitamin_k_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Thiamin (mg)"
              name="thiamin_mg"
              value={formData.thiamin_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Riboflavin (mg)"
              name="riboflavin_mg"
              value={formData.riboflavin_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Niacin (mg)"
              name="niacin_mg"
              value={formData.niacin_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Pantothenic Acid (mg)"
              name="pantothenic_acid_mg"
              value={formData.pantothenic_acid_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Copper (mg)"
              name="copper_mg"
              value={formData.copper_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Manganese (mg)"
              name="manganese_mg"
              value={formData.manganese_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Selenium (mcg)"
              name="selenium_mcg"
              value={formData.selenium_mcg}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Caffeine (mg)"
              name="caffeine_mg"
              value={formData.caffeine_mg}
              onChange={onChange}
              placeholder="Enter value"
            />
          </Row>
        </div>

        <div>
          <h6 className="mb-3">Fatty Acids</h6>
          <Row className="g-3">
            <NumberField
              label="Monounsaturated Fat (g)"
              name="monounsaturated_fat_g"
              value={formData.monounsaturated_fat_g}
              onChange={onChange}
              placeholder="Enter value"
            />
            <NumberField
              label="Polyunsaturated Fat (g)"
              name="polyunsaturated_fat_g"
              value={formData.polyunsaturated_fat_g}
              onChange={onChange}
              placeholder="Enter value"
            />
          </Row>
        </div>
      </SectionCard>

      <FoodActionBar
        isEditMode={isEditMode}
        saving={saving}
        loadingFood={loadingFood}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </Row>
  </div>
);
